import { useCallback, useEffect, useState } from "react";
import { emptyProgress, progressStore, type CourseProgress } from "@/core/progress";
import type { Course } from "@/core/types";

export interface AllProgress {
  loaded: boolean;
  /** Fortschritt je Kurs-ID; fehlt ein Kurs, ist noch nichts erledigt. */
  get(courseId: string): CourseProgress;
  reset(courseId: string): void;
}

/** Lädt den Fortschritt aller Kurse – die Browser-Ansicht zeigt sie nebeneinander. */
export function useAllProgress(courses: Course[]): AllProgress {
  const [byCourse, setByCourse] = useState<Record<string, CourseProgress>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    void Promise.all(
      courses.map(async (course) => [course.id, await progressStore.load(course.id)] as const),
    ).then((entries) => {
      if (!active) return;
      setByCourse(Object.fromEntries(entries));
      setLoaded(true);
    });
    return () => {
      active = false;
    };
  }, [courses]);

  const get = useCallback(
    (courseId: string) => byCourse[courseId] ?? emptyProgress,
    [byCourse],
  );

  const reset = useCallback((courseId: string) => {
    setByCourse((current) => ({ ...current, [courseId]: { ...emptyProgress } }));
    void progressStore.reset(courseId);
  }, []);

  return { loaded, get, reset };
}
